import { useState } from 'react';
import type { EsmsBrandname } from '../../types/esms';
import InfoTooltip from '../revenue/InfoTooltip';
import EsmsErrorBox from './EsmsErrorBox';

function fmt(v: number): string { return v.toLocaleString('en-US'); }

function successRate(success: number, total: number): string {
  if (total <= 0) return '—';
  return `${((success / total) * 100).toFixed(1)}%`;
}

interface Props {
  brandnames: EsmsBrandname[];
  isDark: boolean;
  isLoading: boolean;
  error: string | null;
}

export default function EsmsBrandnameSection({ brandnames, isDark, isLoading, error }: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  return (
    <div className={`backdrop-blur rounded-lg p-4 sm:p-6 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        Brandnames
        <InfoTooltip isDark={isDark} text={'Danh sách brandname đã đăng ký + thống kê sử dụng trong kỳ.\nNhấn vào hàng để xem breakdown theo loại tin.\n\nNguồn: eSMS GetListBrandname + aggregate report.esms_brandname_daily.'} />
      </h3>

      {isLoading ? (
        <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Đang tải...</div>
      ) : error ? (
        <EsmsErrorBox error={error} isDark={isDark} compact />
      ) : brandnames.length === 0 ? (
        <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Chưa có dữ liệu brandname</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={isDark ? 'border-b border-white/10' : 'border-b border-gray-200'}>
                <th className={`text-left px-2 sm:px-3 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Brandname</th>
                <th className={`text-left px-2 sm:px-3 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Type</th>
                <th className={`text-right px-2 sm:px-3 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>SMS</th>
                <th className={`text-right px-2 sm:px-3 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Chi phí (VND)</th>
                <th className={`text-right px-2 sm:px-3 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Success Rate</th>
              </tr>
            </thead>
            <tbody>
              {brandnames.map((b) => {
                const isOpen = expanded.has(b.brandname);
                return (
                  <>
                    <tr
                      key={b.brandname}
                      onClick={() => toggle(b.brandname)}
                      className={`cursor-pointer ${isDark ? 'border-b border-white/5 hover:bg-white/5' : 'border-b border-gray-100 hover:bg-gray-50'}`}
                    >
                      <td className={`px-2 sm:px-3 py-2 ${isDark ? 'text-slate-200' : 'text-gray-800'}`}>
                        <span className="inline-block w-3 mr-1 opacity-60">{isOpen ? '−' : '+'}</span>
                        {b.brandname}
                      </td>
                      <td className={`px-2 sm:px-3 py-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{b.type}</td>
                      <td className={`px-2 sm:px-3 py-2 text-right font-mono ${isDark ? 'text-cyan-300' : 'text-gray-900'}`}>{fmt(b.sms_count)}</td>
                      <td className={`px-2 sm:px-3 py-2 text-right font-mono ${isDark ? 'text-amber-300' : 'text-gray-900'}`}>{fmt(b.total_cost)}</td>
                      <td className={`px-2 sm:px-3 py-2 text-right font-mono ${isDark ? 'text-emerald-300' : 'text-gray-900'}`}>{successRate(b.success_count, b.sms_count)}</td>
                    </tr>
                    {isOpen && b.byType.length > 0 && (
                      <tr key={b.brandname + '-detail'} className={isDark ? 'bg-white/5' : 'bg-gray-50'}>
                        <td colSpan={5} className="px-2 sm:px-3 py-2">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                                <th className="text-left px-2 py-1 font-medium">Loại</th>
                                <th className="text-right px-2 py-1 font-medium">SMS</th>
                                <th className="text-right px-2 py-1 font-medium">Thành công</th>
                                <th className="text-right px-2 py-1 font-medium">Thất bại</th>
                                <th className="text-right px-2 py-1 font-medium">Chi phí (VND)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {b.byType.map((t) => (
                                <tr key={t.smsType} className={isDark ? 'text-slate-200' : 'text-gray-800'}>
                                  <td className="px-2 py-1">{t.label} <span className="opacity-60">({t.smsType})</span></td>
                                  <td className={`px-2 py-1 text-right font-mono ${isDark ? 'text-cyan-300' : 'text-gray-900'}`}>{fmt(t.sms_count)}</td>
                                  <td className={`px-2 py-1 text-right font-mono ${isDark ? 'text-emerald-300' : 'text-gray-900'}`}>{fmt(t.success_count)}</td>
                                  <td className={`px-2 py-1 text-right font-mono ${isDark ? 'text-rose-300' : 'text-gray-900'}`}>{fmt(t.failed_count)}</td>
                                  <td className={`px-2 py-1 text-right font-mono ${isDark ? 'text-amber-300' : 'text-gray-900'}`}>{fmt(t.total_cost)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
